import * as React from "react";
import { useField, useFormikContext } from "formik";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EyeIcon, EyeOffIcon } from "lucide-react";

interface FormikPasswordInputProps extends React.ComponentProps<"input"> {
    name: string;
}

const FormikPasswordInput = React.forwardRef<
    HTMLInputElement,
    FormikPasswordInputProps
>(({ className, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [field] = useField(props.name);
    const { errors } = useFormikContext<{
        [key: string]: unknown;
    }>();

    const hasErrors = !!errors[props.name];

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="relative">
            <Input
                className={cn(
                    hasErrors ? "border-red-500" : "",
                    "pr-10",
                    className,
                )}
                ref={ref}
                type={showPassword ? "text" : "password"}
                {...field}
                {...props}
            />
            <Button
                className="absolute right-0 top-0 h-full px-3 py-2 text-neutral-500 hover:text-neutral-600"
                onClick={togglePasswordVisibility}
                size="icon"
                tabIndex={-1}
                type="button"
                variant="link"
            >
                {showPassword ? (
                    <EyeOffIcon className="h-4 w-4" />
                ) : (
                    <EyeIcon className="h-4 w-4" />
                )}
                <span className="sr-only">
                    {showPassword ? "Hide password" : "Show password"}
                </span>
            </Button>
        </div>
    );
});

FormikPasswordInput.displayName = "FormikPasswordInput";

export { FormikPasswordInput };
